import React, {useState} from "react";
import {useRecoilValue, useSetRecoilState} from "recoil";
import {
    globalBrandModuleSettingState,
    globalColorThemeState,
    globalEWalletCurrencySettingState,
    globalEWalletSummaryState,
    globalIconListState,
    globalNotificationState,
    globalUserDetailState,
    globalUserEWalletState,
    globalUserWalletState,
} from "./global_state";
import {LoadingOutlined} from "@ant-design/icons";
import {getClientEWallets, getEWalletById, getEWalletSummaryBalance} from "../api/graphql/eWallet";
import {useTranslation} from "react-i18next";
import {Tooltip} from "antd";

function useShared() {
    const {t} = useTranslation();

    const userDetail = useRecoilValue(globalUserDetailState);
    const iconListState = useRecoilValue(globalIconListState);
    const colorState = useRecoilValue(globalColorThemeState);
    const brandModuleSetting = useRecoilValue(globalBrandModuleSettingState);
    const eWalletCurrencySetting = useRecoilValue(globalEWalletCurrencySettingState);
    const setUserWalletState = useSetRecoilState(globalUserWalletState);
    const setUserEWalletState = useSetRecoilState(globalUserEWalletState);
    const setEWalletSummaryState = useSetRecoilState(globalEWalletSummaryState);
    const setNotificationMessage = useSetRecoilState(globalNotificationState);

    const formatCurrency = (currencyObject, isTotal = false, hideCurrency = false) => {
        if (!currencyObject || !Object.keys(currencyObject)?.length) return null;

        const {totalBalance, currencyCode, balance} = currencyObject;
        const code = currencyCode || "";

        const {digit = 0} = eWalletCurrencySetting?.find(w => w?.currencyCode === currencyCode) || {};

        const raw = isTotal ? totalBalance : balance;

        const cleaned = typeof raw === "string" ? raw.replace(/,/g, "") : raw;

        const num = Number(cleaned);

        if (isNaN(num)) return (0).toFixed(digit);

        const sign = num < 0 ? "-" : "";
        const value = Math.abs(num).toFixed(digit);

        if (!code || hideCurrency) return `${sign}${value}`;

        return `${sign}${code} ${value}`;
    };

    const getIconImage = (name, isActive = false, isDefault = true) => {
        const obj = iconListState.find((item) => item?.name === name);
        if (obj) {
            const color = colorState === "dark" ? "Dark" : "Light";
            const active = isActive === true ? "Active" : "Inactive";
            const defaultIcon = isDefault === true ? "defaultIcon" : "icon";
            return obj[`${defaultIcon}${color}${active}`][0]?.path;
        } else {
            return false;
        }
    };

    const IconCommon = ({
                            iconName,
                            onClick,
                            className,
                            isAlwaysActive,
                            style,
                            title,
                            isModule,
                            isAlwaysInActive,
                            tooltipText = "",
                            shade,
                        }) => {
        const colorState = useRecoilValue(globalColorThemeState);
        const color = shade || (colorState === "dark" ? "Dark" : "Light");
        const [isCommonHovered, setIsCommonHovered] = useState(false);
        const activeSuffix = (isCommonHovered || isAlwaysActive) && !isAlwaysInActive ? "Active" : "Inactive";
        const filterIcon = iconListState.find((item) => item?.name === iconName);
        const iconPath =
            filterIcon === undefined ||
            filterIcon[`icon${color}${activeSuffix}`] === null
                ? null
                : filterIcon[`icon${color}${activeSuffix}`][0]?.path;

        const onMouseEnter = () => setIsCommonHovered(true);
        const onMouseLeave = () => setIsCommonHovered(false);

        if (iconPath !== "") {
            return (
                <Tooltip title={tooltipText}>
                    <div
                        className={"iconContainer"}
                        onMouseEnter={onMouseEnter}
                        onMouseLeave={onMouseLeave}
                        style={{height: "100%"}}
                    >
                        <img
                            alt={""}
                            style={style}
                            className={className}
                            onClick={onClick}
                            src={iconPath}
                        />
                        {title && (
                            <p className={"mt-1 text-center"}>
                                {t(isModule ? `module.${title}` : `common.${title}`)}
                            </p>
                        )}
                    </div>
                </Tooltip>
            );
        } else {
            return <LoadingOutlined/>;
        }
    };

    const IconModule = ({
                            iconName,
                            className,
                            title,
                            active = true,
                            isAlwaysActive,
                            layout,
                        }) => {
        const colorState = useRecoilValue(globalColorThemeState);
        const color = colorState === "dark" ? "Dark" : "Light";
        const [isModuleHovered, setIsModuleHovered] = useState(false);
        const newNaming = layout === 6 || layout === 5 || layout === 3 || layout === 1;

        const activeSuffix = (isModuleHovered && active) || isAlwaysActive ? "Active" : "Inactive";
        const filterIcon = (newNaming ? iconListState : brandModuleSetting)?.find(
            (item) => item?.name === iconName
        );

        const iconPath =
            filterIcon === undefined ||
            filterIcon[`icon${color}${activeSuffix}`] === null
                ? null
                : filterIcon[`icon${color}${activeSuffix}`][0]?.path;
        const onMouseEnter = () => setIsModuleHovered(true);
        const onMouseLeave = () => setIsModuleHovered(false);

        if (iconPath !== "") {
            return (
                <div
                    className="iconContainer"
                    onMouseEnter={onMouseEnter}
                    onMouseLeave={onMouseLeave}
                    style={{height: "100%"}}
                >
                    <img alt={""} className={className} src={iconPath}/>
                    {title && <p className={"mt-1"}>{t(`module.${title}`)}</p>}
                </div>
            );
        } else {
            return <LoadingOutlined/>;
        }
    };

    const IconFlag = ({
                          countryCode,
                          onClick,
                          className,
                          isAlwaysActive,
                          style,
                          title,
                          isModule,
                          isAlwaysInActive,
                      }) => {
        const colorState = useRecoilValue(globalColorThemeState);
        const color = colorState === "dark" ? "Dark" : "Light";
        const [isCommonHovered, setIsCommonHovered] = useState(false);
        const activeSuffix =
            (isCommonHovered || isAlwaysActive) && !isAlwaysInActive
                ? "Active"
                : "Inactive";
        const filterIcon = iconListState.find(
            (item) => item?.countryCode === countryCode
        );
        const iconPath =
            filterIcon === undefined ||
            filterIcon[`icon${color}${activeSuffix}`] === null
                ? null
                : filterIcon[`icon${color}${activeSuffix}`][0]?.path;

        const onMouseEnter = () => setIsCommonHovered(true);
        const onMouseLeave = () => setIsCommonHovered(false);

        if (iconPath !== "") {
            return (
                <div
                    className="iconContainer"
                    onMouseEnter={onMouseEnter}
                    onMouseLeave={onMouseLeave}
                    style={{height: "100%"}}
                >
                    <img
                        alt={""}
                        style={style}
                        className={className}
                        onClick={onClick}
                        src={iconPath}
                    />
                    {title && (
                        <p className={"mt-1 text-center"}>
                            {t(isModule ? `module.${title}` : `common.${title}`)}
                        </p>
                    )}
                </div>
            );
        } else {
            return <LoadingOutlined/>;
        }
    };

    const convertLanguageFile = (record) => {
        let resources = {};
        record.forEach((item) => {
            resources[item?.code] = {translation: item?.translationFile};
        });

        return resources;
    };

    const updateEWallet = async () => {
        try {
            const eWallet = await getEWalletById(userDetail?.eWallet?._id);
            setUserWalletState(eWallet?.getEWalletByID);

            const eWallets = await getClientEWallets({});
            setUserEWalletState(eWallets?.getClientEWallets?.data);

            const summary = await getEWalletSummaryBalance({})
            setEWalletSummaryState(summary?.getEWalletSummaryBalance?.data?.find(w => w?.currencyCode === "USD") ?? {})
        } catch (e) {
            console.error("Error:", e);
            setNotificationMessage({
                status: "error",
                title: "",
                msg: "System Error",
                key: "systemNotification",
            });
        }
    };

    const detectViewable = (name) => {
        return brandModuleSetting?.find(
            (module) => module?.isViewable === true && module.name === name
        );
    };

    return {
        formatCurrency,
        getIconImage,
        IconModule,
        IconCommon,
        IconFlag,
        convertLanguageFile,
        updateEWallet,
        detectViewable,
    };
}

export default useShared;